import {
  EnphaseLogo,
  SungrowLogo,
  ChevronRight,
  ViessmannLogo,
  ElectricalServices,
} from "src/assets";
import {
  Box,
  Alert,
  Stack,
  Skeleton,
  Snackbar,
  CardMedia,
  SnackbarCloseReason,
} from "@mui/material";
import {
  SESSION_KEYS,
  SITE_TECHNICAL_DATA,
  UI_FLOW_PREFERENCES,
} from "src/consts/session";
import {
  hemProviders,
  setHemProvider,
  leadSelfOnboardingTransfer,
} from "src/api/selfOnboardingClients";
import { PvSetupTypes } from "../PvSetup";
import { WallboxTypes } from "../Wallbox";
import { useTranslation } from "react-i18next";
import { SmartMeterTypes } from "../SmartMeter";
import Text from "src/components/Displays/Text";
import { BatterySetupTypes } from "../BatterySetup";
import { numberFormat } from "src/helpers/numberFormat";
import { useCallback, useEffect, useState } from "react";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { useNavigate, useSearchParams } from "react-router-dom";
import FormContainer from "src/components/Displays/FormContainer";
import { ROUTES, SELF_ONBOARDING_ROUTES } from "src/consts/routes";

const Compatibility = () => {
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("translation");
  const transferId = searchParams.get("tid") || "";
  const salesPartnerUuid = searchParams.get("spid") || "";

  const [isLoading, setIsLoading] = useState({
    oems: true,
    setOem: false,
  });

  const [, setTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [, setOemSelected] = useSessionStorage<string>(
    SESSION_KEYS.SELF_ONBOARDING_OEM_SELECTED,
  );
  const [getSessionStorageItem] = useSessionStorage<string>(
    SESSION_KEYS.TRANSFER_ID,
  );
  const [, setUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );
  const [, setSiteTechnicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );

  const [allHemsProviders, setAllhemsProviders] = useState<
    {
      logicalId: string;
      displayName: string;
    }[]
  >([]);

  const initialCall = useCallback(async () => {
    try {
      const sessionTransferId = getSessionStorageItem();
      if (sessionTransferId && sessionTransferId === transferId) {
        const allHemsProviders = await hemProviders(transferId);
        setAllhemsProviders(allHemsProviders.hemProviders);
        setIsLoading((prevValues) => ({
          ...prevValues,
          oems: false,
        }));
        return;
      }

      const response = await leadSelfOnboardingTransfer({
        transferId,
        salesPartnerUuid,
      });
      setTransferId(transferId);
      setUiFlowPreferences({
        language: response.uiFlowPreferences.language,
        callbackUrlSuccess: response.uiFlowPreferences.callbackUrlSuccess,
        callbackUrlFailure: response.uiFlowPreferences.callbackUrlFailure,
        processType: response.uiFlowPreferences.processType,
        skipOemAuth: response.uiFlowPreferences.skipOemAuth.toString(),
      });

      setSiteTechnicalData({
        pvSetup: {
          pvInstalled: response.siteTechnicalData.pvInstalled
            ? PvSetupTypes.JA
            : PvSetupTypes.NEIN,

          pvPowerMaxKwp: numberFormat(
            response.siteTechnicalData.pvPowerMaxKwp
              ? response.siteTechnicalData.pvPowerMaxKwp.toString()
              : "",
          ),
        },

        wallboxInstalled: response.siteTechnicalData.wallboxInstalled
          ? WallboxTypes.JA
          : WallboxTypes.NEIN,

        batterySetup: {
          batteryInstalled: response.siteTechnicalData.batteryInstalled
            ? BatterySetupTypes.JA
            : BatterySetupTypes.NEIN,

          batteryCapacity: numberFormat(
            response.siteTechnicalData.batterySizeKwh
              ? response.siteTechnicalData.batterySizeKwh.toString()
              : "",
          ),
        },

        smartMeterInstalled: response.siteTechnicalData.smartMeterInstalled
          ? SmartMeterTypes.JA
          : SmartMeterTypes.NEIN,
        heatpumpInstalled:
          response.siteTechnicalData.heatpumpInstalled?.toString(),
        marketLocationId: response.siteTechnicalData.marketLocationId,
        meterNumber: response.siteTechnicalData.meterNumber,
      });

      if (response.siteTechnicalData.hemProviderLogicalId) {
        setOemSelected(response.siteTechnicalData.hemProviderLogicalId);
        navigate(ROUTES.PV_SETUP);
      }

      const allHemsProviders = await hemProviders(transferId);
      setAllhemsProviders(allHemsProviders.hemProviders);
      setIsLoading((prevValues) => ({
        ...prevValues,
        oems: false,
      }));
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialCall();
  }, [initialCall]);

  const getHemsLogo = (logicalId: string) => {
    let logo = "";
    switch (logicalId) {
      case "VIESSMANN_API":
        logo = ViessmannLogo;
        break;
      case "SUNGROW_ISOLARCLOUD":
        logo = SungrowLogo;
        break;
      case "ENPH_VPPAPI":
        logo = EnphaseLogo;
        break;
      default:
        break;
    }
    return logo;
  };

  const handleNextClick = async (logicalId: string) => {
    try {
      setIsLoading((prevValues) => ({
        ...prevValues,
        setOem: true,
      }));
      await setHemProvider(transferId, logicalId);

      setTimeout(() => {
        setIsLoading((prevValues) => ({
          ...prevValues,
          setOem: false,
        }));

        navigate(`${SELF_ONBOARDING_ROUTES.STATUS}?oem=${logicalId}`);
        setOemSelected(logicalId);
      }, 1000);
    } catch (e) {
      const error = e as Error;
      setMsg(error.msgId ?? "");
      setOpen(true);
      setIsLoading((prevValues) => ({
        ...prevValues,
        setOem: false,
      }));
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  return (
    <Stack gap={5}>
      <Text
        text={t("page.selfOnboardingCompatibility.pageTitle")}
        sx={(theme) => ({
          fontSize: "32px",
          lineHeight: "35px",
          fontFamily: "isonType",
          color: theme.palette.mainFont.primary,
        })}
      />

      <Text
        text={t("page.selfOnboardingCompatibility.pageSubTitle")}
        sx={(theme) => ({
          fontSize: "14px",
          lineHeight: "24px",
          color: theme.palette.mainFont.primary,
        })}
      />

      <Box sx={{ maxWidth: { xs: "100%", sm: "362px" } }}>
        <FormContainer onSubmit={() => {}}>
          <Stack gap={1} direction={"row"} alignItems={"center"}>
            <ElectricalServices />
            <Text
              text={t("page.selfOnboardingCompatibility.formTitle")}
              sx={(theme) => ({
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack sx={{ marginTop: 6, marginBottom: 3 }} gap={3}>
            {isLoading.oems && (
              <Skeleton
                variant="text"
                sx={{
                  fontSize: "4rem",
                  borderRadius: "12px",
                }}
              />
            )}

            {allHemsProviders &&
              allHemsProviders.map((hems) => (
                <PrimaryButton
                  fullWidth
                  isLoading={isLoading.setOem}
                  onClick={() => handleNextClick(hems.logicalId)}
                  text={""}
                  startIcon={
                    <CardMedia
                      component="img"
                      alt="hemsLogo"
                      sx={{ objectFit: "contain" }}
                      image={getHemsLogo(hems.logicalId)}
                    />
                  }
                  endIcon={<ChevronRight />}
                  sx={(theme) => ({
                    display: "flex",
                    justifyContent: "space-between",
                    color: theme.palette.primaryButton.text,
                    backgroundColor: "#fff",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "22px",
                    borderRadius: "12px",
                    border: "1px solid #DEDEDE",
                    "&:hover": {
                      backgroundColor: "#fff",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                      cursor: "not-allowed",
                      pointerEvents: "all !important",
                      color: theme.palette.primaryButton.text,
                    },
                    padding: "18px 24px 18px 24px",
                    textTransform: "none",
                  })}
                />
              ))}
          </Stack>
        </FormContainer>
      </Box>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          severity="error"
          variant="filled"
          onClose={handleClose}
          sx={{ width: "100%" }}
        >
          {msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Compatibility;
